import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { mutualisteAPI, formatCurrency } from '../../services/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/UI/card';
import { Button } from '../../components/UI/button';
import { Badge } from '../../components/UI/badge';
import {
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  CreditCard,
  ArrowLeft,
  Filter,
  Calendar,
  Printer
} from 'lucide-react';

/**
 * PAGE: HISTORIQUE DES PAIEMENTS MUTUALISTES
 * 
 * Affiche tous les paiements SAUF les paiements de masse (BULK_ENTERPRISE)
 * Filtres: Date début, Date fin, Produits, Type de paiement (sans masse), Mode de paiement, Opérateur, Matricule
 */

const PaymentHistoryMutualiste = () => {
  const navigate = useNavigate();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    product: '',
    paymentType: '',
    mutualiste: '',
    paymentMethod: '',
    operator: '',
    startDate: '',
    endDate: ''
  });

  const [showFilters, setShowFilters] = useState(true);
  const [products, setProducts] = useState([]);
  
  // États pour l'autocomplétion mutualiste
  const [mutualistes, setMutualistes] = useState([]);
  const [showMutualisteDropdown, setShowMutualisteDropdown] = useState(false);
  const [mutualisteInput, setMutualisteInput] = useState('');
  const mutualisteRef = useRef(null);

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);

  useEffect(() => {
    loadProducts();
  }, []);

  // Synchroniser mutualisteInput avec filters.mutualiste
  useEffect(() => {
    if (filters.mutualiste === '' && mutualisteInput !== '') {
      setMutualisteInput('');
    }
  }, [filters.mutualiste, mutualisteInput]);

  // Fermer le dropdown en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mutualisteRef.current && !mutualisteRef.current.contains(event.target)) {
        setShowMutualisteDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadProducts = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/offers`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setProducts(response.data.offers || []);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');

      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      params.append('excludeType', 'BULK_ENTERPRISE'); // Exclure les paiements de masse
      
      if (filters.product) params.append('offerId', filters.product);
      if (filters.paymentType) params.append('paymentType', filters.paymentType);
      if (filters.mutualiste) params.append('mutualisteSearch', filters.mutualiste);
      if (filters.paymentMethod) params.append('paymentMethod', filters.paymentMethod);
      if (filters.operator) params.append('operator', filters.operator);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPayments(response.data.data || []);
      setPagination(response.data.pagination || pagination);
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      toast.error('Impossible de charger les paiements');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Recherche de mutualistes
  const searchMutualistes = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setMutualistes([]);
      setShowMutualisteDropdown(false);
      return;
    }

    try {
      const response = await mutualisteAPI.searchMutualistes({ search: searchTerm });
      const mutualistesData = response.data?.data?.mutualistes || response.data?.mutualistes || [];
      setMutualistes(mutualistesData);
      setShowMutualisteDropdown(true);
    } catch (error) {
      console.error('Erreur recherche mutualistes:', error);
      setMutualistes([]);
    }
  };

  const handleMutualisteInputChange = (value) => {
    setMutualisteInput(value);
    searchMutualistes(value);
  };

  const handleSelectMutualiste = (mutualiste) => {
    const displayValue = `${mutualiste.matricule} - ${mutualiste.nom} ${mutualiste.prenoms}`;
    setMutualisteInput(displayValue);
    handleFilterChange('mutualiste', displayValue);
    setShowMutualisteDropdown(false);
  };

  const handleMutualisteBlur = () => {
    // Appliquer le filtre quand l'utilisateur quitte le champ
    if (mutualisteInput !== filters.mutualiste) {
      handleFilterChange('mutualiste', mutualisteInput);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'PENDING':
      case 'PROCESSING':
        return <Clock className="w-4 h-4 text-orange-600" />;
      case 'FAILED':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'CANCELLED':
        return <AlertCircle className="w-4 h-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      COMPLETED: 'success',
      PENDING: 'warning',
      PROCESSING: 'warning',
      FAILED: 'destructive',
      CANCELLED: 'default'
    };
    const labels = {
      COMPLETED: 'Complété',
      PENDING: 'En attente',
      PROCESSING: 'En traitement',
      FAILED: 'Échoué',
      CANCELLED: 'Annulé'
    };
    return { variant: variants[status] || 'default', label: labels[status] || status };
  };

  const getPaymentTypeLabel = (type) => {
    const labels = {
      SELF_SERVICE: 'Self-service',
      THIRD_PARTY: 'Tiers'
    };
    return labels[type] || type;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      ESPECES: 'Espèces',
      VIREMENT: 'Virement',
      CHEQUE: 'Chèque',
      CARTE_BANCAIRE: 'Carte bancaire',
      MOBILE_MONEY: 'Mobile Money',
      ORANGE_MONEY: 'Orange Money',
      MTN_MONEY: 'MTN Money',
      MOOV_MONEY: 'Moov Money',
      WAVE: 'Wave'
    };
    return labels[method] || method;
  };

  const handleReissueReceipt = async (payment) => {
    try {
      const token = localStorage.getItem('accessToken');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments/${payment.id}/receipt-duplicate`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `duplicata-${payment.receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Duplicata téléchargé avec succès');
    } catch (error) {
      console.error('Erreur génération duplicata:', error);
      toast.error('Erreur lors de la génération du duplicata');
    }
  };

  if (loading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Bouton retour */}
      <button
        onClick={() => navigate('/admin/payments/mutualistes')}
        className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        <span className="text-sm font-medium">Retour aux paiements mutualistes</span>
      </button>

      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 text-green-600 mr-4">
              <CreditCard className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary-900 mb-1">
                Historique des Paiements Mutualistes
              </h1>
              <p className="text-gray-600">
                {pagination.total} paiement(s) enregistré(s)
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtres
          </Button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Date début */}
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date début
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                />
              </div>

              {/* Date fin */}
              <div>
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Date fin
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                />
              </div>

              {/* Produits */}
              <div>
                <label className="text-sm font-medium">Produits</label>
                <select
                  value={filters.product}
                  onChange={(e) => handleFilterChange('product', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="">Tous les produits</option>
                  {Array.isArray(products) && products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type de paiement (sans "Masse") */}
              <div>
                <label className="text-sm font-medium">Type de paiement</label>
                <select
                  value={filters.paymentType}
                  onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="">Tous les types</option>
                  <option value="SELF_SERVICE">Self-service</option>
                  <option value="THIRD_PARTY">Tiers</option>
                </select>
              </div>

              {/* Mode de paiement */}
              <div>
                <label className="text-sm font-medium">Mode de paiement</label>
                <select
                  value={filters.paymentMethod}
                  onChange={(e) => {
                    handleFilterChange('paymentMethod', e.target.value);
                    if (e.target.value !== 'MOBILE_MONEY') {
                      handleFilterChange('operator', '');
                    }
                  }}
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                >
                  <option value="">Tous les modes</option>
                  <option value="ESPECES">Espèces</option>
                  <option value="VIREMENT">Virement</option>
                  <option value="CHEQUE">Chèque</option>
                  <option value="CARTE_BANCAIRE">Carte bancaire</option>
                  <option value="MOBILE_MONEY">Mobile Money</option>
                </select>
              </div>

              {/* Opérateur (uniquement si Mobile Money) */}
              {filters.paymentMethod === 'MOBILE_MONEY' && (
                <div>
                  <label className="text-sm font-medium">Opérateur</label>
                  <select
                    value={filters.operator}
                    onChange={(e) => handleFilterChange('operator', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                  >
                    <option value="">Tous les opérateurs</option>
                    <option value="ORANGE_MONEY">Orange Money</option>
                    <option value="MTN_MONEY">MTN Money</option>
                    <option value="MOOV_MONEY">Moov Money</option>
                    <option value="WAVE">Wave</option>
                  </select>
                </div>
              )}

              {/* Matricule (avec autocomplétion) */}
              <div className="relative" ref={mutualisteRef}>
                <label className="text-sm font-medium">Matricule</label>
                <input
                  type="text"
                  value={mutualisteInput}
                  onChange={(e) => handleMutualisteInputChange(e.target.value)}
                  onBlur={handleMutualisteBlur}
                  onFocus={() => mutualisteInput.length >= 2 && setShowMutualisteDropdown(true)}
                  placeholder="Matricule ou nom..."
                  className="w-full mt-1 px-3 py-2 border rounded-md"
                />
                {showMutualisteDropdown && mutualistes.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {mutualistes.map((mutualiste) => (
                      <div
                        key={mutualiste.id}
                        onClick={() => handleSelectMutualiste(mutualiste)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        <div className="font-medium">{mutualiste.matricule}</div>
                        <div className="text-sm text-gray-600">{mutualiste.nom} {mutualiste.prenoms}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des paiements */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {payments.length === 0 ? (
          <div className="py-12 text-center">
            <CreditCard className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucun paiement</h3>
            <p className="text-gray-600">
              Aucun paiement ne correspond à vos critères
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reçu N°
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mutualiste
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Méthode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => {
                  const statusBadge = getStatusBadge(payment.status);

                  return (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {payment.receiptNumber || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600">
                                {payment.mutualiste 
                                  ? `${payment.mutualiste.prenoms?.charAt(0) || ''}${payment.mutualiste.nom?.charAt(0) || ''}` 
                                  : 'U'
                                }
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {payment.mutualiste 
                                ? `${payment.mutualiste.prenoms} ${payment.mutualiste.nom}`
                                : 'Non renseigné'
                              }
                            </div>
                            <div className="text-sm text-gray-500">
                              {payment.mutualiste?.matricule || payment.externalRef || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-semibold text-primary-600">
                          {formatCurrency(payment.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getPaymentMethodLabel(payment.paymentMethod)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="outline">
                          {getPaymentTypeLabel(payment.paymentType)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={statusBadge.variant}>
                          <span className="flex items-center space-x-1">
                            {getStatusIcon(payment.status)}
                            <span>{statusBadge.label}</span>
                          </span>
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {payment.receiptNumber && payment.status === 'COMPLETED' && (
                            <button
                              onClick={() => handleReissueReceipt(payment)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Imprimer duplicata"
                            >
                              <Printer className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Page <span className="font-medium">{pagination.page}</span> sur{' '}
                  <span className="font-medium">{pagination.totalPages}</span> ({pagination.total} paiements)
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1 || loading}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-l-md"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages || loading}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-r-md"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentHistoryMutualiste;
